import { useCalendarTime } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarTimeContext";
import LazyImage from "components/lazy-image";
import { PUBLIC_ASSETS } from "constants/publicAssets";
import moment from "moment-timezone";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectFocusedSessionNextStatus,
  showBlockIntervalDetails,
  showGcalEventDetails,
  showSessionDetails,
} from "store/calendarSidebarSlice";
import formatSessionDuration from "utils/formatSessionDuration";
import getClasses from "utils/getClasses";
import SessionIndicatorIcons from "../../../../calendar-view/components/session-indicator-icons/SessionIndicatorIcons";
import { DayPreviewEvent } from "../day-preview.types";
import styles from "./DayPreviewBadge.module.scss";

interface DayPreviewBadgeProps {
  event: DayPreviewEvent;
}

const DayPreviewBadge = ({ event }: DayPreviewBadgeProps) => {
  const dispatch = useDispatch();

  const { currentCalendarTimezone } = useCalendarTime();
  const focusedSessionNextStatus = useSelector(selectFocusedSessionNextStatus);

  const startTimeString = useMemo(
    () => moment.tz(event.startISO, currentCalendarTimezone).format("hh:mm A"),
    [event.startISO, currentCalendarTimezone],
  );

  const durationMinutes = useMemo(
    () => moment(event.endISO).diff(moment(event.startISO), "minutes"),
    [event.startISO, event.endISO],
  );

  const isActive = useMemo(
    () =>
      new Date(event.startISO).valueOf() <= Date.now() &&
      Date.now() <= new Date(event.endISO).valueOf(),
    [event.startISO, event.endISO],
  );

  const isInvited = useMemo(
    () =>
      event.type === "appointment" && event.item.statusFlags.unconfirmedInvite,
    [event.type, event.item],
  );

  const typeIndicatorStyle = useMemo(() => {
    if (event.isCancelled) {
      return "cancelled";
    }
    if (event.type === "appointment") {
      if (isActive) {
        return "appointment--active";
      }
      if (isInvited) {
        return "appointment--invited";
      }
      return "appointment";
    }
    if (event.type === "groupSession") {
      if (isActive) {
        return "group-session--active";
      }
      return "group-session";
    }
    return "";
  }, [event.isCancelled, isActive, isInvited, event.type]);

  const onClick = useCallback(() => {
    switch (event.type) {
      case "appointment": {
        dispatch(
          showSessionDetails({
            appointment: event.item,
            nextStatus: focusedSessionNextStatus,
          }),
        );
        return;
      }
      case "groupSession": {
        //TODO show group session details in calendar sidebar
        return;
      }
      case "gcalEvent": {
        dispatch(showGcalEventDetails(event.item));
        return;
      }
      case "blockedInterval": {
        dispatch(showBlockIntervalDetails(event.item));
      }
    }
  }, [event.type, event.item, focusedSessionNextStatus, dispatch]);

  return (
    <div
      className={getClasses(
        styles["container"],
        event.type === "blockedInterval" &&
          styles["container--blocked-interval"],
      )}
      onClick={onClick}
    >
      <div
        className={getClasses(
          styles["type-indicator"],
          typeIndicatorStyle && styles[`type-indicator--${typeIndicatorStyle}`],
        )}
      />
      <div
        className={getClasses(
          styles["content"],
          event.type === "gcalEvent" && styles["content--gcal-event"],
        )}
      >
        {event.type === "gcalEvent" && (
          <div className={styles["column"]}>
            <LazyImage
              className={styles["gcal-icon"]}
              src={PUBLIC_ASSETS.gcalIcon}
              width={16}
              height={16}
            />
          </div>
        )}
        <div className={styles["column"]}>
          <div className={styles["time"]}>{startTimeString}</div>
          <div className={styles["duration"]}>
            {formatSessionDuration(durationMinutes)}
          </div>
        </div>
        <div className={styles["column"]}>
          <div
            className={getClasses(
              styles["title"],
              event.isCancelled && styles["title--cancelled"],
            )}
          >
            {event.title}
          </div>
          <div className={styles["subtitle-row"]}>
            {event.sessionTypeColor && (
              <div
                className={styles["session-type-indicator"]}
                style={{
                  backgroundColor: event.sessionTypeColor,
                }}
              ></div>
            )}{" "}
            <div className={styles["subtitle"]}>{event.subtitle}</div>
            {event.sessionIndicatorIconsData && (
              <SessionIndicatorIcons
                className={styles["icons"]}
                data={event.sessionIndicatorIconsData}
                maxDisplayedIcons={4}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DayPreviewBadge;
